import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, CheckCircle, Save, RotateCcw, Plus, Package } from 'lucide-react';
import { TransferTable } from './TransferTable';
import { AfternoonOrderTable } from './AfternoonOrderTable';
import { BreadTransferSession, TransferShift, UpdateTransferRowPayload } from '../types/bread-transfers.types';

interface TransferTabProps {
  shift: TransferShift;
  session: BreadTransferSession | null;
  onCreateSession: (shift: TransferShift) => void;
  onUpdateRow: (shift: TransferShift, payload: UpdateTransferRowPayload) => void;
  onCompleteSession: (shift: TransferShift) => void;
  onResetSession: (shift: TransferShift) => void;
  loading: boolean;
}

export const TransferTab: React.FC<TransferTabProps> = ({
  shift,
  session,
  onCreateSession,
  onUpdateRow,
  onCompleteSession,
  onResetSession,
  loading
}) => {
  const [afternoonTotalPedido, setAfternoonTotalPedido] = useState(0);

  const calculateAfternoonTotals = () => {
    const mockData = {
      'MAC 01 (ANGAMOS)': { historic: 430, totalPrevious: 276, weekPrevious: 371, quantity: 260 },
      'MAC 02 (LA ROTONDA)': { historic: 345, totalPrevious: 250, weekPrevious: 311, quantity: 175 },
      'MAC 03 (BENAVIDES)': { historic: 274, totalPrevious: 253, weekPrevious: 274, quantity: 95 },
      'MAC 04 (SURQUILLO)': { historic: 123, totalPrevious: 71, weekPrevious: 94, quantity: 53 },
      'MAC 05 (UPC)': { historic: 112, totalPrevious: 120, weekPrevious: 108, quantity: 44 },
      'MAC 07 (LA FONTANA)': { historic: 196, totalPrevious: 149, weekPrevious: 184, quantity: 136 },
      'MAC 08 (ULIMA)': { historic: 159, totalPrevious: 133, weekPrevious: 159, quantity: 39 },
      'MAC 09 (MIRAFLORES)': { historic: 121, totalPrevious: 70, weekPrevious: 121, quantity: 81 }
    };
    
    return Object.values(mockData).reduce((totals, data) => ({
      historicAvgSaleTotal: totals.historicAvgSaleTotal + data.historic,
      totalSalePreviousDay: totals.totalSalePreviousDay + data.totalPrevious,
      totalSalePreviousWeek: totals.totalSalePreviousWeek + data.weekPrevious,
      quantityToRequest: totals.quantityToRequest + data.quantity,
      safetyFactor: 0,
      requestToBakery: totals.requestToBakery + data.quantity // Inicialmente igual a quantity
    }), { historicAvgSaleTotal: 0, totalSalePreviousDay: 0, totalSalePreviousWeek: 0, quantityToRequest: 0, safetyFactor: 0, requestToBakery: 0 });
  };

  const shiftLabel = shift === 'morning' ? 'Mañana' : 'Tarde';
  const isCompleted = session?.status === 'completed';


  const handleUpdateRow = (payload: UpdateTransferRowPayload) => {
    onUpdateRow(shift, payload);
  };

  const handleComplete = () => {
    onCompleteSession(shift);
  };

  if (!session) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader className="text-center py-12">
            <CardTitle className="text-xl text-gray-700">
              No hay transferencia de {shiftLabel.toLowerCase()} para esta fecha
            </CardTitle>
            <CardDescription>
              Crea una nueva sesión de transferencia para comenzar
            </CardDescription>
            <div className="pt-4">
              <Button
                onClick={() => onCreateSession(shift)}
                disabled={loading}
                className="bg-macartur-primary hover:bg-macartur-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Transferencia de {shiftLabel}
              </Button>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Transferencias {shiftLabel}
          </h2>
          {/* Only show badge for completed status */}
          {isCompleted && (
            <Badge 
              variant="default"
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Completado
            </Badge>
          )}
        </div>
        
        <div className="flex gap-2">
          {!isCompleted && shift !== 'afternoon' && (
            <Button
              onClick={handleComplete}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Generar Transferencia
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {shift === 'afternoon' ? (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Pedido a Panadería</CardTitle>
                <CheckCircle className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {afternoonTotalPedido}
                </div>
                <p className="text-xs text-muted-foreground">
                  total pedido
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total a Solicitar</CardTitle>
                <Package className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {calculateAfternoonTotals().quantityToRequest}
                </div>
                <p className="text-xs text-muted-foreground">
                  unidades a solicitar
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Venta Día Anterior</CardTitle>
                <AlertTriangle className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {calculateAfternoonTotals().totalSalePreviousDay}
                </div>
                <p className="text-xs text-muted-foreground">
                  día anterior
                </p>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total a Despachar</CardTitle>
                <CheckCircle className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {session.totals.calculatedDispatch}
                </div>
                <p className="text-xs text-muted-foreground">
                  unidades programadas
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Stock Total</CardTitle>
                <Package className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {session.totals.initialStock}
                </div>
                <p className="text-xs text-muted-foreground">
                  unidades disponibles
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Descarte Total</CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {session.totals.waste}
                </div>
                <p className="text-xs text-muted-foreground">
                  unidades descartadas
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Transfer Table */}
      {shift === 'afternoon' ? (
        <AfternoonOrderTable
          locations={session.locations}
          totals={calculateAfternoonTotals()}
          onUpdateRow={handleUpdateRow}
          onTotalChange={setAfternoonTotalPedido}
          disabled={isCompleted}
        />
      ) : (
        <TransferTable
          locations={session.locations}
          totals={session.totals}
          shift={shift}
          onUpdateRow={handleUpdateRow}
          disabled={isCompleted}
        />
      )}

    </div>
  );
};